import React, { useEffect, useState } from "react";
import { useUser } from "../context/useUser";
import { useNavigate } from "react-router-dom";
import Header from "../components/header";
import Footer from "../components/footer";
import HousingCompanyInfoCard from "../components/housingCompanyInfoCard";
import { apiFetch } from "../api/client";
import { getImageUrl } from "../utils/imageUrl";

const url = process.env.REACT_APP_BACKEND_CONNECTION;

export default function Cart() {
  const { user, selectedCompany } = useUser();
  const navigate = useNavigate();
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [voteStatus, setVoteStatus] = useState(null);
  const [voteResults, setVoteResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [voteCountReady, setVoteCountReady] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [votingBusy, setVotingBusy] = useState(false);
  const [remindingUserId, setRemindingUserId] = useState(null);
  const [reminderStatuses, setReminderStatuses] = useState({});
  const [reminderPopup, setReminderPopup] = useState({ open: false, missing: [] });
  const hasItems = Object.keys(items).length > 0;
  const userRole = selectedCompany?.role;
  const canManageSelections = ["chairman", "selection_delegate"].includes(userRole);
  const currentVoteStatus = voteStatus?.status || "incomplete";
  const canFinalizeSelections = currentVoteStatus === "complete" && voteCountReady;
  const canSubmitToDesigner = canManageSelections && canFinalizeSelections;
  const voteStatusLabel = {
    incomplete: "Puheenjohtaja tai valintadelegaatti tekee valintoja",
    vote_launched: "Hallituksen äänestys käynnissä",
    launch_count: "Äänten laskenta käynnissä",
    complete: "Äänestys valmis",
  }[currentVoteStatus] || currentVoteStatus;

  const fetchVoteResults = async ({ showLoader = false } = {}) => {
    if (!selectedCompany?.summaryId || !user?.token) return [];

    if (showLoader) setResultsLoading(true);

    try {
      const data = await apiFetch(`/form/summary/${selectedCompany.summaryId}/vote/results`, {
        token: user.token,
      });

      if (data?.status) {
        setVoteStatus((prev) => ({ ...(prev || {}), status: data.status }));
      }

      const results = Array.isArray(data?.results) ? data.results : [];
      setVoteResults(results);
      setVoteCountReady(true);
      return results;
    } catch (err) {
      if (err?.status === 409) {
        setVoteResults([]);
        setVoteCountReady(false);
        return [];
      }
      throw err;
    } finally {
      if (showLoader) setResultsLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.token) return;

    if (!selectedCompany?.summaryId || !selectedCompany?.staircase_type_id) {
      setLoading(false);
      return;
    }

    async function fetchCompanyInfo() {
    try {
      const res = await fetch(`${url}/user/housing-company/${selectedCompany.housing_company_id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      const data = await res.json();
      setCompanyInfo(data);
    } catch (err) {
      console.error("Failed to load housing company:", err);
      }
    }

    if (selectedCompany?.housing_company_id) {
      fetchCompanyInfo();
    }

    if (!selectedCompany?.summaryId) {
      setLoading(false);
      return;
    }

    async function fetchCartItems() {
      try {
        const summaryData = await apiFetch(`/form/summary/current?housing_company_id=${selectedCompany.housing_company_id}&staircase_type_id=${selectedCompany.staircase_type_id}`, {
          token: user.token,
        });
        setSummary(summaryData);

        const voteStatusData = await apiFetch(`/form/summary/${selectedCompany.summaryId}/vote/status`, {
          token: user.token,
        });
        setVoteStatus(voteStatusData);

        const data = await apiFetch(`/form/summary/${selectedCompany.summaryId}/selections`, {
          token: user.token,
        });

        if (["launch_count", "complete"].includes(voteStatusData?.status)) {
          await fetchVoteResults({ showLoader: voteStatusData.status === "launch_count" });
        } else {
          setVoteResults([]);
          setVoteCountReady(false);
        }

        const grouped = data.reduce((acc, item) => {
          const parent = item.parent_category_name || "Muu";
          if (!acc[parent]) acc[parent] = {};
          if (!acc[parent][item.category_name]) acc[parent][item.category_name] = [];
          acc[parent][item.category_name].push(item);
          return acc;
        }, {});

        setItems(grouped);
      } catch (err) {
        console.error("Failed to fetch cart items:", err);
      } finally {
        setLoading(false);
      }
    }

    fetchCartItems();
  }, [user, selectedCompany]);

  const handleDelete = async (itemId) => {
    if (!window.confirm("Haluatko varmasti poistaa tämän valinnan?")) return;

    try {
      await apiFetch(`/form/summary/item/${itemId}`, {
        method: "DELETE",
        token: user.token,
      });

      setItems((prev) => {
        const updated = { ...prev };
        for (const parent in updated) {
          for (const category in updated[parent]) {
            updated[parent][category] = updated[parent][category].filter(opt => opt.id !== itemId);
          }
        }
        for (const parent in updated) {
          for (const category in updated[parent]) {
            if (updated[parent][category].length === 0) delete updated[parent][category];
          }
          if (Object.keys(updated[parent]).length === 0) delete updated[parent];
        }
        return updated;
      });

      window.dispatchEvent(new Event("cart-updated"));
    } catch (err) {
      console.error("Failed to delete item:", err);
    }
  };
  
  const handleDownloadPDF = async () => {
    try {
      const res = await fetch(
        `${url}/form/summary/${selectedCompany.summaryId}/pdf`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (!res.ok) throw new Error("PDF generation failed");

      const blob = await res.blob();
      const fileURL = window.URL.createObjectURL(blob);

      let filename = "valinnat.pdf";
      const disposition = res.headers.get("content-disposition");
      if (disposition) {
        const match = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
        if (match && match[1]) filename = match[1];
      }

      const link = document.createElement("a");
      link.href = fileURL;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download PDF:", err);
    }
  };

  const refreshVoteData = async () => {
    if (!selectedCompany?.summaryId || !user?.token) return;

    const [voteStatusData, selectionsData] = await Promise.all([
      apiFetch(`/form/summary/${selectedCompany.summaryId}/vote/status`, { token: user.token }),
      apiFetch(`/form/summary/${selectedCompany.summaryId}/selections`, { token: user.token }),
    ]);

    setVoteStatus(voteStatusData);

    const grouped = selectionsData.reduce((acc, item) => {
      const parent = item.parent_category_name || "Muu";
      if (!acc[parent]) acc[parent] = {};
      if (!acc[parent][item.category_name]) acc[parent][item.category_name] = [];
      acc[parent][item.category_name].push(item);
      return acc;
    }, {});

    setItems(grouped);
  };

  const handleLaunchVote = async () => {
    if (!selectedCompany?.summaryId || !user?.token) return;
    if (!window.confirm("Haluatko varmasti avata äänestyksen? Äänestyksen alkamisen jälkeen valintoihin ei voi enää tehdä muutoksia.")) return;

    try {
      setVotingBusy(true);
      await apiFetch(`/form/summary/${selectedCompany.summaryId}/vote/launch`, {
        method: "POST",
        token: user.token,
      });

      setReminderStatuses({});
      setReminderPopup({ open: false, missing: [] });
      await refreshVoteData();
      alert("Äänestys avattu hallituksen jäsenille.");
    } catch (err) {
      alert(err?.body?.error || "Äänestyksen avaaminen epäonnistui");
    } finally {
      setVotingBusy(false);
    }
  };

  const handleLaunchCount = async () => {
    if (!selectedCompany?.summaryId || !user?.token) return;

    try {
      setVotingBusy(true);
      setVoteStatus((prev) => ({ ...(prev || {}), status: "launch_count" }));
      await apiFetch(`/form/summary/${selectedCompany.summaryId}/vote/count`, {
        method: "POST",
        token: user.token,
      });

      await fetchVoteResults({ showLoader: true });
      setReminderStatuses({});
      setReminderPopup({ open: false, missing: [] });
      await refreshVoteData();
      alert("Äänten laskenta valmis. Tulokset päivitetty.");
    } catch (err) {
      if (err?.status === 409 && Array.isArray(err?.body?.missing)) {
        setReminderStatuses({});
        setReminderPopup({ open: true, missing: err.body.missing });
        await refreshVoteData();
        return;
      }
      await refreshVoteData();
      alert(err?.body?.error || "Äänten laskennan käynnistys epäonnistui");
    } finally {
      setVotingBusy(false);
    }
  };

  const handleSendReminder = async (boardMemberId) => {
    if (!selectedCompany?.summaryId || !user?.token) return;

    try {
      setRemindingUserId(boardMemberId);
      setReminderStatuses((prev) => ({ ...prev, [boardMemberId]: "sending" }));
      const data = await apiFetch(
        `/form/summary/${selectedCompany.summaryId}/vote/remind/${boardMemberId}`,
        {
          method: "POST",
          token: user.token,
        }
      );

      setReminderStatuses((prev) => ({ ...prev, [boardMemberId]: "sent" }));
      alert(data?.message || "Muistutus lähetetty");
    } catch (err) {
      setReminderStatuses((prev) => ({ ...prev, [boardMemberId]: "idle" }));
      alert(err?.body?.error || "Muistutuksen lähetys epäonnistui");
    } finally {
      setRemindingUserId(null);
    }
  };

  const handleSubmitToDesigner = async () => {
    if (!canFinalizeSelections) {
      alert("Valinnat voi lähettää suunnitteluun vasta, kun äänestystulokset ovat valmiit.");
      return;
    }

    if (!window.confirm("Haluatko varmasti lähettää valinnat suunnittelijalle?")) return;

    try {
      await apiFetch(`/form/summary/${selectedCompany.summaryId}/submit`, {
        method: "POST",
        token: user.token,
      });

      alert("Valinnat lähetetty suunnittelijalle!");
    } catch (err) {
      console.error(err);
      alert(err?.body?.error || "Lähetys epäonnistui");
    }
  };

  const handleContinue = () => {
    if (!summary) return;
    const nextPage = summary.last_step || "waterpipes";
    navigate(`/${nextPage}`);
  };

  const handleBack = () => {
    navigate("/taloyhtio-ja-rapputyyppi");
  };

  return (
    <>
  <Header />
  <main className="cart-page-container">

    {loading ? (
      <p className="cart-loading">Ladataan valintoja...</p>

    ) : !hasItems ? (
      <div className="cart-empty-page">
        <p>Sinulla ei ole vielä valintoja.</p>
        <button onClick={handleBack} className="default-button">
          Aloita Valinta
        </button>
      </div>

    ) : (
      <>
        <h1 className="default-title">Yhteenveto</h1>

        <HousingCompanyInfoCard companyInfo={companyInfo} />

        <div className="choices-container card-box">
          <p className="category-title">Valinnat</p>

          <div className="cart-items-container default-text">
            {Object.entries(items).map(([parentName, categories]) => (
              <div key={parentName}>
                <p><strong>{parentName}</strong></p>

                {Object.entries(categories).map(([categoryName, options]) => (
                  <React.Fragment key={categoryName}>
                    {options.map(opt => (
                      <div key={opt.selection_id} className="cart-item-row">
                      <div className="cart-item-image-container">
                        {opt.image ? (
                          <img
                            src={getImageUrl(opt.image)}
                            alt={opt.name}
                            className="cart-item-image"
                          />
                        ) : (
                          <div className="cart-item-placeholder">Ei kuvaa</div>
                        )}
                      </div>

                      <div className="cart-item-category-container">
                        <h3 className="cart-category-name">{categoryName}</h3>
                      </div>

                      <div className="cart-item-name-container">
                        <h4 className="cart-option-name">{opt.name}</h4>
                      </div>

                      {canManageSelections && currentVoteStatus === "incomplete" && opt.selection_id && (
                        <p
                          className="cart-delete-button"
                          onClick={() => handleDelete(opt.selection_id)}
                        >
                          <i className="fa-solid fa-xmark" />
                        </p>
                      )}
                    </div>
                  ))}
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="choices-container card-box">
          <p className="category-title">Äänestystila</p>
          <p className="default-text">
            Tila: <strong>{voteStatusLabel}</strong>
          </p>

          {(currentVoteStatus === "launch_count" || currentVoteStatus === "complete") && (
            <div className="default-text" style={{ marginTop: "16px" }}>
              <p><strong>Äänestystulokset</strong></p>

              {resultsLoading && (
                <p>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /> Lasketaan tuloksia...
                </p>
              )}

              {!resultsLoading && voteResults.length === 0 && (
                <p>Tuloksia ei löytynyt.</p>
              )}

              {!resultsLoading && voteResults.length > 0 && voteResults.map((result) => (
                <div
                  key={`result-${result.selectable_item_id}`}
                  className="cart-item-row"
                  style={{ alignItems: "center", gap: "12px" }}
                >
                  <div className="cart-item-name-container">
                    <h4 className="cart-option-name"><strong>{result.name}</strong></h4>
                    <p>
                      {result.parent_category_name ? `${result.parent_category_name} / ` : ""}
                      {result.category_name || "Muu"}
                    </p>
                    <p>
                      Ääniä: puolta {result.agree_count || 0} / eriävä {result.alternative_count || 0} - Voittava: <strong>{result.winning_name || result.name}</strong>
                    </p>
                    {result.was_changed && (
                      <p>
                        Valinta muuttui äänestyksessä: {result.name} <i className="fa-solid fa-arrow-right-long" aria-hidden="true" /> {result.winning_name || result.winning_alternative_name}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {canManageSelections && (
            <div className="button-row">
              {currentVoteStatus === "incomplete" && (
                <button
                  className="cart-button"
                  onClick={handleLaunchVote}
                  disabled={votingBusy}
                >
                  Avaa äänestys hallitukselle
                </button>
              )}

              {currentVoteStatus === "vote_launched" && (
                <button
                  className="cart-button"
                  onClick={handleLaunchCount}
                  disabled={votingBusy}
                >
                  Käynnistä äänten laskenta
                </button>
              )}
            </div>
          )}
        </div>

        <div className="bottom-container">
          <p className="default-text">
            Voit tulostaa valinnat pdf-tiedostona Tulosta PDF-painikkeella.
            Suunnittelijalle lähettämisen jälkeen valintoja ei voi tässä palvelussa muuttaa.
          </p>

          <div className="button-row">
            {canManageSelections && (
              <button onClick={handleContinue} className="cart-button secondary">
                Jatka valintoja
              </button>
            )}

            <button
              className="cart-button secondary"
              onClick={handleDownloadPDF}
              disabled={!canFinalizeSelections}
            >
              Lataa PDF
            </button>

            {canManageSelections && (
              <button
                className="cart-button"
                onClick={handleSubmitToDesigner}
                disabled={!canSubmitToDesigner}
              >
                Hyväksy ja lähetä suunnitteluun
              </button>
            )}
          </div>
        </div>
      </>
    )}
  </main>

  {reminderPopup.open && (
    <div
      className="popup-overlay"
      onClick={() => {
        setReminderStatuses({});
        setReminderPopup({ open: false, missing: [] });
      }}
    >
      <div className="popup-box reminder-popup" onClick={(e) => e.stopPropagation()}>
        <p className="warning-title">Kaikki hallituksen jäsenet eivät ole vielä äänestäneet</p>
        <p className="reminder-popup-intro default-text">
          Voit lähettää muistutuksen niille hallituksen jäsenille, joilta ääniä vielä puuttuu.
        </p>
        <div className="reminder-popup-list">
        {reminderPopup.missing.map((member) => (
          <div key={`missing-${member.user_id}`} className="reminder-popup-item">
            <div className="reminder-popup-member-copy">
              <p className="default-text reminder-popup-member-name">{member.name}</p>
              <p className="default-text reminder-popup-member-meta">
                Ei ole vielä äänestänyt. Puuttuvia ääniä: {member.missing_votes}
              </p>
            </div>
            <button
              className={`cart-button secondary reminder-popup-button ${reminderStatuses[member.user_id] === "sent" ? "is-sent" : ""}`}
              disabled={remindingUserId === member.user_id || reminderStatuses[member.user_id] === "sent"}
              onClick={() => handleSendReminder(member.user_id)}
            >
              {reminderStatuses[member.user_id] === "sent"
                ? "Lähetetty"
                : remindingUserId === member.user_id
                  ? "Lähetetään..."
                  : "Lähetä muistutus"}
            </button>
          </div>
        ))}
        </div>

        <div className="button-row reminder-popup-actions">
          <button
            className="cart-button secondary"
            onClick={() => {
              setReminderStatuses({});
              setReminderPopup({ open: false, missing: [] });
            }}
          >
            Sulje
          </button>
        </div>
      </div>
    </div>
  )}
  <Footer />
</>
  );
}
